import * as yup from 'yup';

// =============================================================================
// VALIDATION PATTERNS
// These patterns MUST match the backend (BusinessDetailsContracts.cs)
// Backend is the source of truth - keep these in sync
// =============================================================================

/**
 * NATIONAL INSURANCE NUMBER (NINO)
 * Format: 2 letters + 6 digits + 1 letter (A-D suffix)
 *
 * Rules per HMRC Manual NIM39110:
 * - First letter: Cannot be D, F, I, Q, U, V
 * - Second letter: Cannot be D, F, I, O, Q, U, V
 * - Prohibited prefixes: BG, GB, KN, NK, NT, TN, ZZ
 * - Suffix must be: A, B, C, or D
 *
 * @see https://www.gov.uk/hmrc-internal-manuals/national-insurance-manual/nim39110
 */
const NINO_PATTERN =
  /^(?!BG|GB|KN|NK|NT|TN|ZZ)(?![DFIQUV])[A-Z](?![DFIOQU])[A-Z]\d{6}[A-D]$/;

/**
 * UNIQUE TAXPAYER REFERENCE (UTR)
 * Format: 10 digits, optionally followed by 'K'
 *
 * Rules per HMRC Design Patterns:
 * - Self-Assessment UTR: 10 digits
 * - May optionally end with 'K' (case insensitive)
 *
 * @see https://design.tax.service.gov.uk/hmrc-design-patterns/unique-taxpayer-reference/
 */
const UTR_PATTERN = /^\d{10}[Kk]?$/;

/**
 * COMPANY REGISTRATION NUMBER (CRN)
 * Format: 8 characters total
 *
 * Rules per Companies House / HMRC COTAX Manual COM40011:
 * - England/Wales: 8 digits
 * - Scotland: SC + 6 digits
 * - Northern Ireland: NI + 6 digits
 * - LLPs: OC, SO, NC + 6 digits
 *
 * @see https://www.gov.uk/hmrc-internal-manuals/cotax-manual/com40011
 */
const CRN_PATTERN = /^(\d{8}|(SC|NI|OC|SO|NC)\d{6})$/;

// =============================================================================
// INPUT NORMALISATION FUNCTIONS
// Per GOV.UK Design System, accept user-friendly formats and normalise before validation
// =============================================================================

/**
 * Normalizes NINO input by removing spaces/hyphens and converting to uppercase
 * GOV.UK recommends accepting formats like "AB 12 34 56 C"
 */
export const normalizeNino = (value: string): string =>
  value.replace(/[\s-]/g, '').toUpperCase();

/**
 * Normalizes UTR input by removing spaces
 * GOV.UK recommends accepting formats like "12345 67890"
 */
export const normalizeUtr = (value: string): string => value.replace(/\s/g, '');

/**
 * Normalizes CRN input by removing spaces and converting to uppercase
 */
export const normalizeCrn = (value: string): string =>
  value.replace(/\s/g, '').toUpperCase();

export type BusinessType = 'soleTrader' | 'limitedCompany';

export interface SoleTraderFormData {
  hmrcFullName: string;
  utr: string;
  nationalInsuranceNumber: string;
}

export interface LimitedCompanyFormData {
  companyName: string;
  utr: string;
  companyRegistrationNumber: string;
}

export interface BusinessDetailsCaptureFormData {
  businessType: BusinessType;
  businessName: string;
  soleTrader: SoleTraderFormData;
  limitedCompany: LimitedCompanyFormData;
}

const soleTraderSchema = yup.object().shape({
  hmrcFullName: yup
    .string()
    .required('HMRC full name is required')
    .max(200, 'HMRC full name must be 200 characters or less')
    .trim(),
  utr: yup
    .string()
    .required('UTR is required')
    .transform((value: string) => (value ? normalizeUtr(value) : value))
    .matches(
      UTR_PATTERN,
      'UTR must be 10 digits (optionally followed by K), like 1234567890'
    )
    .trim(),
  nationalInsuranceNumber: yup
    .string()
    .required('National Insurance Number is required')
    .transform((value: string) => (value ? normalizeNino(value) : value))
    .matches(
      NINO_PATTERN,
      'Enter a National Insurance number in the format AB123456C'
    )
    .trim(),
});

const limitedCompanySchema = yup.object().shape({
  companyName: yup
    .string()
    .required('Company name is required')
    .max(200, 'Company name must be 200 characters or less')
    .trim(),
  utr: yup
    .string()
    .required('Company UTR is required')
    .transform((value: string) => (value ? normalizeUtr(value) : value))
    .matches(
      UTR_PATTERN,
      'Company UTR must be 10 digits (optionally followed by K), like 1234567890'
    )
    .trim(),
  companyRegistrationNumber: yup
    .string()
    .required('Company Registration Number is required')
    .transform((value: string) => (value ? normalizeCrn(value) : value))
    .matches(
      CRN_PATTERN,
      'Company Registration Number must be 8 digits or 2 letters (SC, NI, OC, SO, NC) followed by 6 digits'
    )
    .trim(),
});

export const businessDetailsCaptureSchema = yup.object().shape({
  businessType: yup
    .string()
    .oneOf(['soleTrader', 'limitedCompany'], 'Please select a business type')
    .required('Please select a business type'),
  businessName: yup
    .string()
    .max(200, 'Business name must be 200 characters or less')
    .trim(),
  soleTrader: yup.object().when('businessType', {
    is: 'soleTrader',
    then: () => soleTraderSchema,
    otherwise: () =>
      yup.object().shape({
        hmrcFullName: yup.string(),
        utr: yup.string(),
        nationalInsuranceNumber: yup.string(),
      }),
  }),
  limitedCompany: yup.object().when('businessType', {
    is: 'limitedCompany',
    then: () => limitedCompanySchema,
    otherwise: () =>
      yup.object().shape({
        companyName: yup.string(),
        utr: yup.string(),
        companyRegistrationNumber: yup.string(),
      }),
  }),
});

export const defaultSoleTraderValues: SoleTraderFormData = {
  hmrcFullName: '',
  utr: '',
  nationalInsuranceNumber: '',
};

export const defaultLimitedCompanyValues: LimitedCompanyFormData = {
  companyName: '',
  utr: '',
  companyRegistrationNumber: '',
};

export const defaultBusinessDetailsValues: BusinessDetailsCaptureFormData = {
  businessType: 'soleTrader',
  businessName: '',
  soleTrader: defaultSoleTraderValues,
  limitedCompany: defaultLimitedCompanyValues,
};
